import React, { useState, useEffect, useCallback, Suspense, lazy, useRef } from "react";
import Navbar from "./navigation";
import ReplyDrawer from "./ReplyDrawer";
import api from "./api/api";
import { ENDPOINTS } from "./api/endpoints";
import stockImg from "./assets/notfound.jpg";
import AppLoader from "./components/common/AppLoader";
import AdminNav from "./admin/AdminNav";
import Footer from "./Footer";
import GlobalNotification from "./components/GlobalNotification";

const SearchFilters = lazy(() => import("./talent/components/SearchFilters"));
const ResultsPanel = lazy(() => import("./talent/components/ResultsPanel"));
const PaginationControl = lazy(() => import("./utils/PaginationControl"));

const TalentSearch = () => {
  const defaultFilters = {
    unionStatus: [],
    sex: [],
    location: [],
    typeOfTalent: [],
    Ethnicity: [],
    vocalRange: [],
    vocalType: [],
    ageMin: "",
    ageMax: "",
    radius: "",
    specialSkills: "",
    eyeColor: "",
    hairColor: "",
    heightFeetMin: "",
    heightInchesMin: "",
    heightFeetMax: "",
    heightInchesMax: "",
    weightMin: "",
    weightMax: "",
    talentLastName: "",
    sortBy: "MostLikes",
  };

  const isFetching = useRef(false);
  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [filters, setFilters] = useState(defaultFilters);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [brokenImageIds, setBrokenImageIds] = useState([]);
  const [isReplyDrawerOpen, setReplyDrawerOpen] = useState(false);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [unionOptions, setUnionOptions] = useState([]);
  const [ethnicityOptions, setEthnicityOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const directorId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const [userId, setUserId] = useState(null);
  const isFirstLoad = useRef(true);

  const handleSearch = useCallback(
    async (page = 1, appliedFilters) => {
      if (isFetching.current) return;
      isFetching.current = true;

      setIsLoading(true);
      setError(null);

      try {
        //always use latest filters
        const f = appliedFilters || filters;

        const heightStartInches =
          (f.heightFeetMin ? f.heightFeetMin * 12 : 0) +
          (f.heightInchesMin ? parseInt(f.heightInchesMin, 10) : 0);

        const heightEndInches =
          (f.heightFeetMax ? f.heightFeetMax * 12 : 0) +
          (f.heightInchesMax ? parseInt(f.heightInchesMax, 10) : 0);

        const { sortBy, ...filtersWithoutSortBy } = f;

const payload = {
  ...filtersWithoutSortBy,
  page,
  pageSize,
  location: f.location?.length > 0 ? f.location.join(",") : undefined,
  sex: f.sex?.length > 0 ? f.sex.join(",") : undefined,
  union: f.unionStatus?.length > 0 ? f.unionStatus.join(",") : undefined,
  Ethnicity: f.Ethnicity?.length > 0 ? f.Ethnicity.join(",") : undefined,
  typeOfTalent:
    f.typeOfTalent?.length > 0 ? f.typeOfTalent.join(",") : undefined,
  vocalRange:
    f.vocalRange?.length > 0 ? f.vocalRange.join(",") : undefined,
  vocalType:
    f.vocalType?.length > 0 ? f.vocalType.join(",") : undefined,
  MustHaveVideo: f.Video || undefined,
  MustHaveAudio: f.Audio || undefined,
  ageStart: f.ageMin || undefined,
  ageEnd: f.ageMax || undefined,
  WeightStart: f.weightMin || undefined,
  WeightEnd: f.weightMax || undefined,
  HeightStart: heightStartInches || undefined,
  HeightEnd: heightEndInches || undefined,
  Distance: f.radius || undefined,
  DirectorId: directorId,
  MostRecent: sortBy === "True",
  MostLikes: !sortBy || sortBy === "MostLikes",
  Experience: sortBy === "Experience",
};

        const response = await api.get(ENDPOINTS.searchTalentProfiles(), {
          params: payload,
        });

        const apiResults = response.data?.data || [];
        const apiTotal = response.data?.count || 0;

        setResults(apiResults);
        setTotalResults(apiTotal);
      } catch (err) {
        setError("Failed to fetch results.");
      } finally {
        isFetching.current = false;
        setIsLoading(false);
      }
    },
    [filters, pageSize, directorId]
  );


  useEffect(() => {
    const id = localStorage.getItem("userId");
    if (id) setUserId(id);
  }, []);

  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      handleSearch(1, filters);
      return;
    }

    if (!isFetching.current) {
      handleSearch(currentPage, filters);
    }
  }, [currentPage]);

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const [locationRes, ethnicityRes, unionRes] = await Promise.all([
          api.get(ENDPOINTS.getLocations()),
          api.get(ENDPOINTS.getEthnicities()),
          api.get(ENDPOINTS.getUnions()),
        ]);
        const locationsData = locationRes.data?.data || [];

        const stateCounts = locationsData.reduce((counts, loc) => {
          const state = (loc.locationState || "").trim().toLowerCase();

          if (state) {
            counts[state] = (counts[state] || 0) + 1;
          }

          return counts;
        }, {});

        setLocationOptions([
          { value: "ALL", label: "All" },
          ...locationsData
            .filter((loc) => loc.locationCode && loc.locationState?.trim())
            .map((loc) => {
              const state = loc.locationState.trim();
              const city = loc.locationCity?.trim();
              const hasMultipleInstances = stateCounts[state.toLowerCase()] > 1;

              return {
                value: loc.locationCode,
                label:
                  hasMultipleInstances && city
                    ? `${state} (${city})`
                    : state,
              };
            }),
        ]);

        const ethnicityData = ethnicityRes.data?.data || [];
        setEthnicityOptions(
          ethnicityData
            .filter((eth) => eth.ethnicityName && eth.activeFlag)
            .map((eth) => ({
              value: eth.ethnicityName,
              label: eth.ethnicityName,
            }))
        );

        const unionData = unionRes.data || [];
        setUnionOptions(
          unionData.map((aff) => ({
            value: aff.affiliationName,
            label: aff.affiliationName,
          }))
        );
      } catch { }
    };

    (async () => {
      await fetchDropdownOptions();
    })();
  }, [handleSearch]);

  const handleMessageClick = (talent) => {
    const enriched = {
      ...talent,
      talentName: `${talent.firstName || talent.userFirstName || ""} ${talent.lastName || talent.userLastName || ""
        }`.trim(),
      noticeId: undefined,
      noticeTitle: undefined,
      originalMessageId: talent.originalMessageId || "0",
    };
    setSelectedTalent(enriched);
    setReplyDrawerOpen(true);
  };

  const handleFavouriteClick = async (e, talentId, currentFave) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!directorId || !talentId) return;

    try {
      const newFave = !currentFave;
      const formData = new FormData();
      formData.append("clientId", directorId);
      formData.append("talentId", talentId);
      formData.append("Fave", newFave);

      await api.post(ENDPOINTS.addOrUpdateFave(), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setResults((prev) =>
        prev.map((profile) =>
          profile.userId === talentId ? { ...profile, favorite: newFave } : profile
        )
      );
    } catch { }
  };

  const handleClearFilters = () => {
    setFilters(defaultFilters);
    setCurrentPage(1);
    handleSearch(1, defaultFilters);
  };

  return (
    <div style={{ position: "relative", minHeight: "100vh" }}>
      {userRole === "ADMIN" ? <AdminNav /> : <Navbar />}
      {userRole === "COMPANY" && (
        <GlobalNotification userId={userId} userRole={userRole} />
      )}
      {/*  Global Loader Overlay */}
      {isLoading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(255,255,255,0.6)",
            zIndex: 1500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AppLoader message="Loading search results..." />
        </div>
      )}

      <Suspense fallback={<div style={{ textAlign: "center", padding: 20 }}>Loading filters…</div>}>
        <div className="filter-container">
          <div className="search-filters">
            <SearchFilters
              filters={filters}
              setFilters={setFilters}
              locationOptions={locationOptions}
              ethnicityOptions={ethnicityOptions}
              unionOptions={unionOptions}
              handleSearch={handleSearch}
              handleClearFilters={handleClearFilters}
              isLoading={isLoading}
              setCurrentPage={setCurrentPage}
            />
          </div>
        </div>
      </Suspense>

      <div className="background-prop">
        <div style={{ maxWidth: "1200px", margin: "20px auto" }}>
          <Suspense fallback={<div style={{ textAlign: "center", padding: 20 }}>Loading results…</div>}>
            <ResultsPanel
              results={results}
              totalResults={totalResults}
              currentPage={currentPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              isLoading={isLoading}
              error={error}
              handleSearch={handleSearch}
              filters={filters}
              setFilters={setFilters}
              handleMessageClick={handleMessageClick}
              handleFavouriteClick={handleFavouriteClick}
              brokenImageIds={brokenImageIds}
              stockImg={stockImg}
              userRole={userRole}
            />
          </Suspense>

          <Suspense fallback={<div style={{ textAlign: "center", padding: 20 }}>Loading pagination…</div>}>
            <PaginationControl
              page={currentPage}
              totalItems={totalResults}
              pageSize={pageSize}
              onPageChange={(newPage) => setCurrentPage(newPage)}
              sx={{ mt: 3 }}
            />
          </Suspense>
        </div>
      </div>

      <ReplyDrawer
        open={isReplyDrawerOpen}
        onClose={() => setReplyDrawerOpen(false)}
        selectedMessage={selectedTalent}
      />
      <Footer />
    </div>
  );
};

export default TalentSearch;
